import type { Membership } from '../@types/Membership';
import type { Actions } from '../actions';
import {
  MEMBERSHIPS_CLOSE_MODAL,
  MEMBERSHIPS_SHOW_ADD_MODAL,
  MEMBERSHIPS_SHOW_EDIT_MODAL,
  RCV_MEMBERSHIPS_CREATE,
  type RCV_MEMBERSHIPS_CREATE_TYPE,
  RCV_MEMBERSHIPS_LIST,
  RCV_MEMBERSHIPS_REMOVE,
  type RCV_MEMBERSHIPS_REMOVE_TYPE,
  RCV_MEMBERSHIPS_UPDATE,
  type RCV_MEMBERSHIPS_UPDATE_TYPE,
  REQ_MEMBERSHIPS_CREATE,
  REQ_MEMBERSHIPS_LIST,
  REQ_MEMBERSHIPS_REMOVE,
  REQ_MEMBERSHIPS_UPDATE,
  REQUEST_FAILED,
} from '../actions/memberships';
import { requestStatus, responseStatus } from '../shared/statuses';

/* ----------------- INITIAL STATE ----------------- */

interface MembershipsState {
  requestStatus: number;
  responseStatus: number;
  previousRequest: number | null;
  memberships: Membership[] | null;
  shouldShowModal: boolean;
  editId: string | null;
}

const _initialState: MembershipsState = {
  requestStatus: requestStatus.WAITING,
  responseStatus: responseStatus.OK,
  previousRequest: null,
  memberships: null,
  shouldShowModal: false,
  editId: null,
};

/* ----------------- STATUS HELPERS ----------------- */

const getSuccessfulResponse = (newState: MembershipsState) => {
  return {
    ...newState,
    requestStatus: requestStatus.WAITING,
    responseStatus: responseStatus.OK,
  };
};

const getPendingRequest = (state: MembershipsState, status: number) => {
  return {
    ...state,
    requestStatus: status,
    responseStatus: responseStatus.PENDING,
  };
};

/* ----------------- SPECIFIC HELPERS ----------------- */

const createMembership = (state: MembershipsState, action: RCV_MEMBERSHIPS_CREATE_TYPE) => {
  return getSuccessfulResponse({
    ...state,
    memberships: [...(state.memberships || []), action.response],
  });
};

const updateMembership = (state: MembershipsState, action: RCV_MEMBERSHIPS_UPDATE_TYPE) => {
  const membershipsWithoutId = (state.memberships || []).filter((m) => m.id !== action.response.id);

  return getSuccessfulResponse({
    ...state,
    memberships: [...membershipsWithoutId, action.response],
  });
};

const removeMembership = (state: MembershipsState, action: RCV_MEMBERSHIPS_REMOVE_TYPE) => {
  const membershipsWithoutId = (state.memberships || []).filter(
    (m) => m.id !== action.membershipId,
  );

  return getSuccessfulResponse({
    ...state,
    memberships: [...membershipsWithoutId],
  });
};

/* ----------------- REDUCER ----------------- */

const manufacturersReducer = (
  state: MembershipsState = _initialState,
  action: Actions,
): MembershipsState => {
  switch (action.type) {
    case MEMBERSHIPS_SHOW_ADD_MODAL:
      return { ...state, shouldShowModal: true, editId: null };

    case MEMBERSHIPS_SHOW_EDIT_MODAL:
      return { ...state, shouldShowModal: true, editId: action.editId };

    case MEMBERSHIPS_CLOSE_MODAL:
      return { ...state, shouldShowModal: false, editId: null };

    case REQ_MEMBERSHIPS_LIST:
      return getPendingRequest(state, requestStatus.FETCHING_LIST);

    case REQ_MEMBERSHIPS_CREATE:
      return getPendingRequest(state, requestStatus.CREATING);

    case REQ_MEMBERSHIPS_UPDATE:
      return getPendingRequest(state, requestStatus.UPDATING);

    case REQ_MEMBERSHIPS_REMOVE:
      return getPendingRequest(state, requestStatus.REMOVING);

    case RCV_MEMBERSHIPS_LIST:
      return getSuccessfulResponse({ ...state, memberships: action.response });

    case RCV_MEMBERSHIPS_CREATE:
      return createMembership({ ...state, shouldShowModal: false, editId: null }, action);

    case RCV_MEMBERSHIPS_UPDATE:
      return updateMembership({ ...state, shouldShowModal: false, editId: null }, action);

    case RCV_MEMBERSHIPS_REMOVE:
      return removeMembership(state, action);

    case REQUEST_FAILED:
      return {
        ...state,
        requestStatus: requestStatus.WAITING,
        responseStatus: responseStatus.FAILED,
        previousRequest: state.requestStatus,
      };

    default:
      return state;
  }
};

export default manufacturersReducer;
